/**
 * Wizard de onboarding (Sprint 7).
 *
 * 4 pasos:
 * 1. Bienvenida — explica qué es Kaptar + qué va a hacer el wizard.
 * 2. Seleccionar municipio — autocomplete que busca contra `/municipalities?q=`.
 * 3. Perfil — `full_name`, `position` (secretario de planeación, alcalde, ...), `phone` opcional.
 * 4. Confirmación — aceptar términos (checkbox) y terminar.
 *
 * Cada paso llama al endpoint correspondiente y, al éxito, avanza. El
 * contexto del municipio (`useMunicipality`) se refetchea después de cada
 * cambio para que AppShell, Radar etc. vean el muni activo inmediatamente.
 *
 * El flujo es resumable: si el usuario abandonó en el paso 2, la próxima
 * vez que entre lo redirigen al wizard y arranca desde donde estaba
 * (backend ya persistió la selección del muni en `tenants` + `users`).
 */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  acceptLegalDocuments,
  completeOnboarding,
  searchMunicipalities,
  selectMunicipality,
  updateOnboardingProfile,
  type MunicipalityListItemDTO,
} from "@/lib/api";
import { LegalConsent } from "@/components/legal/LegalConsent";
import type { LegalDocId } from "@/legal/documentos";
import { legalConsentErrorMessage } from "@/legal/errores";
import { LOGIN_ACCESO_RETIRADO } from "@/lib/acceso-retirado";
import { INTERNATIONAL_PHONE_ENABLED } from "@/lib/features";
import { useMunicipality } from "@/lib/municipality";
import {
  POSITION_OPTIONS,
  POSITION_OTHER_SENTINEL,
  resolveInitialPosition,
  resolvePositionForSubmit,
} from "@/lib/positions";

import { CuentaSinVincular } from "./CuentaSinVincular";
import { determineStartStep, hasRealName, stepsForUser, type Step } from "./onboarding-steps";

export function Onboarding() {
  const navigate = useNavigate();
  const { context, refetch, isLoading } = useMunicipality();

  // Defense in depth: si `onboarded_at` viene del backend, ese flag es
  // ahora per-user (Sprint 10 fix). Pero como guarda contra regresión,
  // también verificamos que el usuario tenga full_name "real" — un
  // tenant compartido cuyo onboarded_at viene de OTRO miembro no
  // debería redirigir a este usuario al radar.
  const isFullyOnboarded = Boolean(
    context && context.has_tenant && context.onboarded_at && hasRealName(context.full_name),
  );

  // El paso inicial se decide con el contexto que haya en el PRIMER render.
  // 7-sep-2026: solo se calculaba cuando el contexto "cambiaba" después de
  // montar, y el caso real es el contrario — `OnboardingGate` ya lo cargó
  // antes de redirigir aquí — así que un invitado con municipio asignado
  // arrancaba en la bienvenida y de ahí a elegir municipio.
  const [step, setStep] = useState<Step>(() =>
    context && !isFullyOnboarded ? determineStartStep(context) : "welcome",
  );

  // Si el contexto llega después (entrada directa a /onboarding), se ajusta
  // durante el render y no en un efecto: así el usuario nunca ve el paso
  // "welcome" pintado un frame antes de saltar al que le corresponde.
  const [prevContext, setPrevContext] = useState(context);
  if (prevContext !== context) {
    setPrevContext(context);
    if (context && !isFullyOnboarded && step === "welcome") {
      setStep(determineStartStep(context));
    }
  }

  // Un invitado no elige municipio: ya se lo asignaron. La barra tiene que
  // contar lo que va a hacer, no lo que el wizard sabe hacer.
  const pasos = stepsForUser(Boolean(context?.has_tenant));

  // La redirección sí es un efecto: toca el router, un sistema externo.
  useEffect(() => {
    if (isFullyOnboarded) {
      navigate("/radar", { replace: true });
    }
  }, [isFullyOnboarded, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  // 7-sep-2026: lo quitaron del equipo. El wizard no tiene nada que
  // ofrecerle (elegir municipio terminaría en el 403 de R-SEC-003): a
  // /login, donde se cierra la sesión y se explica el motivo.
  if (context?.access_revoked) {
    return <Navigate to={LOGIN_ACCESO_RETIRADO} replace />;
  }

  // Issue #624: sin municipio y sin ser staff, el buscador no puede
  // funcionar. Por R-SEC-003 solo el staff crea o se une a un tenant, así
  // que cada elección terminaba en un 403 y el wizard no tenía salida. Esta
  // pantalla dice qué pasa, qué hacer y ofrece cerrar sesión. El staff sí
  // sigue por el wizard: elegir municipio es como entra a dar soporte.
  if (context && !context.has_tenant && context.is_staff !== true) {
    return <CuentaSinVincular />;
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center p-4 sm:p-6">
      {/*
        Sprint 10 · 5: se quitó el botón "Cerrar sesión" del onboarding.
        Razón: el wizard es un funnel crítico de conversión de invitados
        a usuarios activos. Permitir cerrar sesión a la mitad solo
        ofrece un escape hatch para abandonar el flujo. Si el usuario
        entró con la cuenta equivocada, puede cerrar la pestaña y
        volver a abrir el magic link (Supabase reenvia uno nuevo).
        Quien no tiene municipio ni invitación no llega acá: ve
        `CuentaSinVincular`, que sí ofrece salir (issue #624).
      */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-md sm:p-6 md:p-8">
        <Progress step={step} steps={pasos} />
        {/* Un invitado ya tiene municipio: de la bienvenida (si llegara a
            verla) pasa a su perfil, nunca a elegir otro municipio. */}
        {step === "welcome" && (
          <WelcomeStep onNext={() => setStep(context?.has_tenant ? "profile" : "municipality")} />
        )}
        {step === "municipality" && (
          <MunicipalityStep
            onSelected={async () => {
              await refetch();
              setStep("profile");
            }}
          />
        )}
        {step === "profile" && (
          <ProfileStep
            initialName={context?.full_name ?? ""}
            initialPosition={context?.position ?? ""}
            onSaved={async () => {
              await refetch();
              setStep("confirm");
            }}
          />
        )}
        {step === "confirm" && (
          <ConfirmStep
            onDone={async () => {
              await refetch();
              navigate("/radar");
            }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pasos
// ---------------------------------------------------------------------------

function Progress({ step, steps }: { step: Step; steps: Step[] }) {
  const order = steps;
  const idx = order.indexOf(step);
  return (
    <ol
      role="list"
      aria-label="Progreso del onboarding"
      data-testid="onboarding-progress"
      className="mb-6 flex items-center justify-between"
    >
      {order.map((s, i) => {
        const isActive = i === idx;
        const isDone = i < idx;
        return (
          <li key={s} className="flex flex-1 items-center gap-2">
            <span
              data-testid={`step-dot-${s}`}
              data-active={isActive}
              data-done={isDone}
              className={
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold " +
                (isDone
                  ? "bg-brand-700 text-white"
                  : isActive
                    ? "bg-brand-50 text-brand-700 ring-2 ring-brand-500"
                    : "bg-slate-100 text-slate-400")
              }
            >
              {i + 1}
            </span>
            {i < order.length - 1 && (
              <span className={"h-0.5 flex-1 " + (isDone ? "bg-brand-500" : "bg-slate-200")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }) {
  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Bienvenido a Kaptar</h1>
      <p className="mt-2 text-sm text-slate-600">
        En 3 pasos configuramos tu cuenta para que puedas ver las convocatorias que aplican a tu
        municipio, crear expedientes y recibir alertas.
      </p>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        <li className="flex gap-2">
          <span className="text-brand-700" aria-hidden="true">
            1.
          </span>
          Elige tu municipio.
        </li>
        <li className="flex gap-2">
          <span className="text-brand-700" aria-hidden="true">
            2.
          </span>
          Completa tu perfil.
        </li>
        <li className="flex gap-2">
          <span className="text-brand-700" aria-hidden="true">
            3.
          </span>
          Aceptas los términos y entras al radar.
        </li>
      </ul>
      <button
        type="button"
        onClick={onNext}
        data-testid="welcome-continue"
        className="mt-6 w-full rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
      >
        Comenzar
      </button>
    </div>
  );
}

function MunicipalityStep({ onSelected }: { onSelected: () => void | Promise<void> }) {
  const [query, setQuery] = useState("");
  const [fetchedResults, setFetchedResults] = useState<MunicipalityListItemDTO[]>([]);
  const [searching, setSearching] = useState(false);
  // Issue #621: si la búsqueda falla (500, sin red) hay que decirlo. Antes el
  // catch vaciaba la lista y la UI mostraba "Sin resultados.", y el usuario
  // nuevo concluía que Kaptar no cubre su municipio.
  const [searchError, setSearchError] = useState<string | null>(null);

  // Con menos de 2 caracteres no mostramos nada. Derivado, no estado: antes
  // se limpiaba con un setState dentro del efecto del debounce.
  const results = query.trim().length < 2 ? [] : fetchedResults;

  // Debounce simple sobre `query`.
  useEffect(() => {
    if (query.trim().length < 2) return;
    // Ver la nota en MunicipalityAutocomplete: el indicador arranca con la
    // búsqueda, no con cada tecla.
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchMunicipalities(query.trim());
        setFetchedResults(data);
        setSearchError(null);
      } catch (err) {
        setFetchedResults([]);
        setSearchError(
          err instanceof Error && err.message
            ? err.message
            : "No pudimos buscar tu municipio. Inténtalo de nuevo en un momento.",
        );
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const selectMutation = useMutation({
    mutationFn: (daneCode: string) => selectMunicipality(daneCode),
    onSuccess: async () => {
      await onSelected();
    },
  });

  // Mientras el backend provisiona (user + tenant + membership = 3-6 SQL
  // queries en Supabase free tier ≈ 1-3s), el wizard queda bloqueado
  // sin feedback. Mostramos un loader explícito para que el usuario
  // sepa que NO se trabó — está procesando.
  if (selectMutation.isPending) {
    return (
      <div
        data-testid="municipality-step-loading"
        className="flex flex-col items-center justify-center py-10 text-center"
      >
        <div
          aria-hidden="true"
          className="mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-brand-700"
        />
        <h2 className="text-base font-semibold text-slate-900">Configurando tu cuenta…</h2>
        <p className="mt-1 max-w-sm text-sm text-slate-600">
          Estamos cargando el perfil de tu municipio: indicadores, región SGR, categoría y brechas
          sectoriales. Esto toma un par de segundos.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900">¿Cuál es tu municipio?</h2>
      <p className="mt-1 text-sm text-slate-600">Busca por nombre o por código DANE.</p>
      <input
        id="onboarding-muni-search"
        type="search"
        autoComplete="off"
        placeholder="Nombre o código DANE…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          // El error de la elección anterior no sigue pegado bajo una
          // búsqueda nueva (issue #624).
          if (selectMutation.isError) selectMutation.reset();
        }}
        className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
      />

      {searching && <p className="mt-2 text-xs text-slate-400">Buscando…</p>}

      {results.length > 0 && (
        <ul
          data-testid="municipality-results"
          className="mt-3 max-h-60 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200"
        >
          {results.map((m) => (
            <li key={m.dane_code}>
              <button
                type="button"
                onClick={() => selectMutation.mutate(m.dane_code)}
                disabled={selectMutation.isPending}
                data-dane={m.dane_code}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="font-semibold text-slate-900">
                  {m.name}
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    {m.department_name} · {m.region_sgr}
                  </span>
                </span>
                <span className="font-mono text-xs text-slate-400">{m.dane_code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {query.trim().length >= 2 && !searching && searchError && (
        <p
          role="alert"
          data-testid="municipality-search-error"
          className="mt-2 text-sm text-red-700"
        >
          {searchError}
        </p>
      )}

      {query.trim().length >= 2 && !searching && !searchError && results.length === 0 && (
        <p className="mt-2 text-sm text-slate-500">Sin resultados.</p>
      )}

      {selectMutation.isError && (
        <p className="mt-2 text-sm text-red-700">
          {selectMutation.error instanceof Error
            ? selectMutation.error.message
            : "Error al seleccionar municipio."}
        </p>
      )}
    </div>
  );
}

function ProfileStep({
  initialName,
  initialPosition,
  onSaved,
}: {
  initialName: string;
  initialPosition: string;
  onSaved: () => void | Promise<void>;
}) {
  const [fullName, setFullName] = useState(initialName.includes("@") ? "" : initialName);
  // Cargo: select estructurado con opciones canónicas + fallback free text.
  // Lógica de resolución vive en lib/positions.ts (testeable por unidad).
  const initialResolved = useMemo(() => resolveInitialPosition(initialPosition), [initialPosition]);
  const [positionSelect, setPositionSelect] = useState(initialResolved.selectValue);
  const [positionOther, setPositionOther] = useState(initialResolved.otherText);
  const position = resolvePositionForSubmit(positionSelect, positionOther);
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Validación de móvil colombiano. Acepta entrada con espacios, guiones,
  // paréntesis o `+` (se strippan). Válido si los dígitos son:
  //   - 10 caracteres empezando en 3 (sin prefijo internacional), o
  //   - 12 caracteres empezando en 573 (con prefijo +57).
  // TODO: al expandir fuera de Colombia, agregar selector de país y
  //       normalizar a E.164 con libphonenumber-js.
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneIsValid =
    (phoneDigits.length === 10 && phoneDigits.startsWith("3")) ||
    (phoneDigits.length === 12 && phoneDigits.startsWith("573"));
  const phoneShowError = phoneTouched && phone.length > 0 && !phoneIsValid;

  const normalizedPhone = phoneIsValid
    ? phoneDigits.length === 10
      ? `+57${phoneDigits}`
      : `+${phoneDigits}`
    : phone.trim();

  const mutation = useMutation({
    mutationFn: () =>
      updateOnboardingProfile({
        full_name: fullName.trim(),
        position: position.trim(),
        phone: normalizedPhone,
      }),
    onSuccess: async () => {
      await onSaved();
    },
  });

  // Los tres campos son obligatorios — el backend los necesita completos
  // para alertas (nombre + teléfono) y expedientes (cargo del responsable).
  const canSubmit =
    fullName.trim().length >= 2 &&
    position.trim().length >= 2 &&
    phoneIsValid &&
    !mutation.isPending;

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900">Completa tu registro</h2>
      <p className="mt-1 text-sm text-slate-600">
        Todos los campos son obligatorios. Esta información se guarda en tu perfil y la usan las
        alertas y los expedientes que crees.
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label
            htmlFor="onboarding-name"
            className="mb-1 block text-xs font-semibold text-slate-700"
          >
            Nombre completo <span className="text-red-600">*</span>
          </label>
          <input
            id="onboarding-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
          />
        </div>
        <div>
          <label
            htmlFor="onboarding-position"
            className="mb-1 block text-xs font-semibold text-slate-700"
          >
            Cargo <span className="text-red-600">*</span>
          </label>
          <select
            id="onboarding-position"
            value={positionSelect}
            onChange={(e) => setPositionSelect(e.target.value)}
            required
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
          >
            <option value="" disabled>
              Selecciona tu cargo
            </option>
            {POSITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {positionSelect === POSITION_OTHER_SENTINEL && (
            <input
              id="onboarding-position-other"
              data-testid="onboarding-position-other"
              placeholder="Especifica tu cargo"
              value={positionOther}
              onChange={(e) => setPositionOther(e.target.value)}
              required
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
            />
          )}
        </div>
        <div>
          <label
            htmlFor="onboarding-phone"
            className="mb-1 block text-xs font-semibold text-slate-700"
          >
            Teléfono <span className="text-red-600">*</span>
          </label>
          <input
            id="onboarding-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="300 123 4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => setPhoneTouched(true)}
            required
            aria-invalid={phoneShowError}
            aria-describedby={phoneShowError ? "phone-error" : "phone-hint"}
            className={`w-full rounded-md border px-3 py-1.5 text-sm focus:ring-1 focus:outline-hidden ${
              phoneShowError
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-slate-300 focus:border-brand-500 focus:ring-brand-500"
            }`}
          />
          {phoneShowError ? (
            <p id="phone-error" role="alert" className="mt-1 text-xs text-red-600">
              Ingresa un celular colombiano válido (10 dígitos empezando con 3).
            </p>
          ) : (
            <p id="phone-hint" className="mt-1 text-xs text-slate-500">
              {INTERNATIONAL_PHONE_ENABLED
                ? "El prefijo internacional se agrega automáticamente."
                : "Números de Colombia únicamente. El prefijo +57 se agrega automáticamente."}
            </p>
          )}
        </div>
      </div>

      {mutation.isError && (
        <p className="mt-2 text-sm text-red-700">
          {mutation.error instanceof Error ? mutation.error.message : "Error al guardar perfil."}
        </p>
      )}

      <button
        type="button"
        data-testid="profile-save"
        onClick={() => mutation.mutate()}
        disabled={!canSubmit}
        className="mt-6 w-full rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
      >
        {mutation.isPending ? "Guardando…" : "Continuar"}
      </button>
    </div>
  );
}

/**
 * Último paso (pantalla B del diseño aprobado el 25-sep-2026): lo esencial
 * en tres tarjetas y las dos casillas obligatorias. Registra la aceptación
 * (`POST /legal/accept`) y después cierra el wizard; el backend se niega a
 * cerrarlo sin esa aceptación.
 */
function ConfirmStep({ onDone }: { onDone: () => void | Promise<void> }) {
  const mutation = useMutation({
    mutationFn: async (versions: Record<LegalDocId, string>) => {
      await acceptLegalDocuments(versions);
      return completeOnboarding();
    },
    onSuccess: async () => {
      await onDone();
    },
  });

  return (
    <div>
      <p className="text-[11px] font-bold tracking-wider text-slate-500 uppercase">Último paso</p>
      <h2 className="mt-0.5 text-lg font-semibold text-slate-900">
        Antes de entrar, lo que aceptas
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Lo esencial de nuestros términos y de cómo tratamos tus datos. El texto completo está a un
        clic.
      </p>

      <ul className="my-5 grid grid-cols-1 gap-2.5 sm:grid-cols-3" data-testid="legal-essentials">
        {ESSENTIALS.map((item) => (
          <li key={item.title} className="flex gap-2.5 rounded-xl border border-slate-200 p-3">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"
            >
              {item.icon}
            </span>
            <span>
              <span className="block text-[13px] font-bold text-slate-900">{item.title}</span>
              <span className="text-xs leading-snug text-slate-600">{item.text}</span>
            </span>
          </li>
        ))}
      </ul>

      <LegalConsent
        submitLabel="Aceptar y entrar al Radar"
        pendingLabel="Guardando tu aceptación…"
        submitTestId="finish-onboarding"
        isPending={mutation.isPending}
        error={mutation.isError ? legalConsentErrorMessage(mutation.error) : null}
        onSubmit={(versions) => mutation.mutate(versions)}
      />
    </div>
  );
}

const ICON_CLASS = "h-4 w-4";

const ESSENTIALS: { title: string; text: string; icon: ReactNode }[] = [
  {
    title: "Qué datos tuyos usamos",
    text: "Nombre, cargo, correo, teléfono y tu actividad en la plataforma.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={ICON_CLASS}
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
      </svg>
    ),
  },
  {
    title: "Para qué",
    text: "Darte acceso, mostrarte convocatorias de tu municipio y avisarte a tiempo.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={ICON_CLASS}
      >
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: "Con quién",
    text: "Solo proveedores técnicos (hosting, correo, IA). No vendemos tus datos.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={ICON_CLASS}
      >
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a4 4 0 0 1 8 0v2" />
      </svg>
    ),
  },
];
